import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { motion } from 'framer-motion'
import usePlan from 'src/hooks/subscriptions/usePlan'
import {
  black,
  blue,
  chibiKenji,
  chibiTomokoCheer,
  chibiRen,
  pricePlanPaperClipDefault,
  pricePlanPaperClipHover,
  priceTierSealMonthly
} from '../styles'
import _ from 'lodash'

import useMobile from 'src/hooks/useMobile'

const paymentStageStyles = css`
  justify-self: center;
  margin-top: 60px;
`

const PlanWrapper = styled(motion.li)<{ selected?: boolean, type: string, paymentStage?: boolean, language?: string }>`
  align-self: start;
  position: relative;
  display: grid;
  grid-template-rows: 50px;
  justify-items: center;
  list-style: none;
  width: 300px;
  background-color: ${({ selected }) => selected ? blue : 'white'};
  border: 1px solid ${({ selected }) => selected ? '#5890CC' : '#ccc'};
  min-height: ${({ paymentStage }) => paymentStage ? 'auto' : '418px'};
 
  &:before {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: -1;
    background: ${({ selected }) => selected ? '#5890CC' : '#e5e5e5'};
    transform: rotate(3deg);
  }

  &:hover {
    background-color: ${({ selected }) => selected ? '' : '#eef5ff'};
    cursor: pointer;
  }

  ${({ language }) => language === 'Korean' && css`
    min-height: auto !important;
    padding-bottom: 40px;
  `}

  @media (max-width: 1025px) {
    ${({ type }) => type === 'yearly' && css`
      grid-column: 1 / -1;
      justify-self: center;
    `}
    min-height: auto;
  }

  ${({ paymentStage }) => paymentStage && paymentStageStyles}
`

const Blurb = styled.p<{ selected?: boolean }>`
  background-color: ${({ selected }) => selected ? '#FFE68C' : '#F6F3EE'};
  border-radius: 12px;
  font-family: Outfit;
  font-weight: 600;
  color: ${black};
  padding: 4px 12px;
  max-height: 2rem;
`

const Paperclip = styled.img`
  grid-row: 1;
  grid-column: 1;
  height: 55px;
  justify-self: end;
  position: relative;
  top: -13px;
  left: -13px;
`

const Chibi = styled.img`
  height: 125px;
  grid-row: 1;
  grid-column: 1;
  align-self: end;
`

const Seal = styled.img`
  position: relative;
  height: 135px;
  grid-row: 1;
  grid-column: 1;
  justify-self: start;
  left: -25px;
  top: -75px;
`

const PlanTitle = styled.h3<{ selected?: boolean }>`
  font-family: 'Tilt Warp';
  font-size: 3rem !important;
  color: ${({ selected }) => selected ? 'white' : black};
  padding: 12px 0 12px 0;
`

const CrossedPrice = styled.p<{ selected?: boolean, type: string }>`
  font-family: 'Outfit';
  font-weight: 600;
  font-size: 1.25rem;
  color: ${({ selected }) => selected ? 'white' : black};
  text-decoration: line-through;
  height: 30px;
  margin-bottom: -6px !important;
  ${({ type, selected }) => type === 'yearly' && `color: ${selected ? '#FFE68C' : '#A1DA24'};`}
`

const Price = styled.p<{ selected?: boolean, type: string }>`
  font-family: 'Outfit';
  font-weight: 600;
  font-size: 1.5rem;
  color: ${({ selected }) => selected ? 'white' : black};

  ${({ type }) => type === 'free' && css`
    font-size: 1rem;
    /* padding: 10px 0 0; */
  `}
  ${({ type, selected }) => type === 'yearly' && `color: ${selected ? '#FFE68C' : '#A1DA24'};`}
  height: 30px;
`

const Features = styled.ul<{ selected?: boolean, language?: string }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Outfit';
  font-weight: 600;
  font-size: 1.1rem;
  color: ${({ selected }) => selected ? 'white' : black};
  list-style: circle;
  padding: 0 24px 0 36px !important;
  align-self: start;
  margin: 48px 0 !important;
  height: ${({ language }) => language === 'Japanese' ? '170px' : '48px'};
`

const ChangePlanButton = styled.button`
  font-family: 'Outfit';
  font-weight: 600;
  font-size: 1.1rem;
  color: white;
  background-color: transparent;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;

  &:hover {
    color: #FFE68C;
    cursor: pointer;
  }
`

const AffiliateDisclaimerText = styled.p<{ selected?: boolean }>`
  font-family: 'Outfit';
  font-size: 1rem;
  color: ${({ selected }) => selected ? 'white' : black};
  text-align: center;
  padding-bottom: 36px;
  height: 76px;
`

const AffiliateDisclaimerEmpty = styled.div<{ selected?: boolean }>`
  height: 76px;
  padding-bottom: 36px;
`

type PlanProps = {
  type: 'free' | 'monthly' | 'yearly'
  selectedPlan: 'free' | 'monthly' | 'yearly' | ''
  onClick: () => void
  paymentStage?: boolean
  registerSuccess?: boolean
  language?: string
}

const chibis = {
  free: chibiKenji,
  monthly: chibiTomokoCheer,
  yearly: chibiRen
}

const blurbs = {
  free: "I'm casually learning Japanese...",
  monthly: 'Set goals and progress each month!',
  yearly: 'Equal to two free months a year!'
}

const blurbsKr = {
  free: 'Limited Course & Lesson Access',
  monthly: 'Every Course & Lesson!',
  yearly: 'Every Course & Lesson!'
}

const affiliateBlurbs = {
  free: 'Limited free plan',
  monthly: '50% off for 3 months!',
  yearly: 'Just $7.48/mo for 1 year!'
}

const newYearBlurbs = {
  ...blurbs,
  yearly: 'New Year\'s Sale! 50% OFF!'
}

const freeFeatures = [
  'First 3 Lessons of Each Primary Course',
  'Customizable Notebook',
  'View Questions Asked by Other Students',
]

const freeFeaturesKr = [
  'First 3 Lessons of Each Primary Course',
]

const freeAffiliateFeatures = [
  'Limited Course & Lesson Access',
  'View Answers to Questions'
]

const freeAffiliateFeaturesKr = [
  'Limited Course & Lesson Access',
]

const monthlyFeatures = [
  '5 Courses with Over 60 Lessons',
  'Customizable Notebook',
  'Access to Exclusive Beta Courses',
  'Get Your Questions Answered by Our Japanese Teachers',
]

const monthlyFeaturesKr = [
  '3 Courses with Over 50 Lessons',
  'Customizable Notebook',
  'Access to Exclusive Beta Courses',
]

const monthlyAffiliateFeatures = [
  'Every Course and Lesson!',
  'Interactive Games!',
  'Your Questions Answered by Japanese Teachers!'
]

const monthlyAffiliateFeaturesKr = [
  'Every Course and Lesson!',
]

const yearlyAffiliateFeatures = [
  ...monthlyAffiliateFeatures,
]

const yearlyAffiliateFeaturesKr = [
  ...monthlyAffiliateFeaturesKr,
]

const yearlyFeatures = [
  ...monthlyFeatures,
  'Two Months Free!',
]

const yearlyFeaturesKr = [
  ...monthlyFeaturesKr,
  'Two Months Free!',
]

const features = {
  free: freeFeatures,
  monthly: monthlyFeatures,
  yearly: yearlyFeatures
}

const featuresKr = {
  free: freeFeaturesKr,
  monthly: monthlyFeaturesKr,
  yearly: yearlyFeaturesKr
}

const affiliateFeatures = {
  free: freeAffiliateFeatures,
  monthly: monthlyAffiliateFeatures,
  yearly: yearlyAffiliateFeatures
}

const affiliateFeaturesKr = {
  free: freeAffiliateFeaturesKr,
  monthly: monthlyAffiliateFeaturesKr,
  yearly: yearlyAffiliateFeaturesKr
}

const Plan: React.FC<PlanProps> = ({ type, selectedPlan, onClick, paymentStage, registerSuccess, language = 'Japanese' }) => {
  const pricedPlan = type === 'monthly' ? 'premium-monthly' : 'premium-annual'
  const { price, base, interval, affiliate } = usePlan(pricedPlan)
  const { base: monthlyBase } = usePlan('premium-monthly')

  console.log('monthly base', monthlyBase)

  // *** some NY edits ***
  const showPreRegisterNYPrice = price === '$77.00' && !registerSuccess
  const newYearsPrice = showPreRegisterNYPrice ? '$129.70' : price
  const newYearsBlurbs = showPreRegisterNYPrice ? blurbs : newYearBlurbs
  // *** end some NY edits ***

  const priceText = type === 'free' ? 'No credit card needed' : `${newYearsPrice}/${interval}${affiliate ? '*' : ''}`
  const selected = selectedPlan === type
  const isMobile = useMobile()

  return (
    <PlanWrapper
      onClick={onClick}
      selected={selected}
      type={type}
      paymentStage={paymentStage}
      layoutId={type}
      layout='position'
      transition={{ type: 'spring', stiffness: 400, damping: 80 }}
      language={language}
    >
      {type === 'monthly' && <Seal src={priceTierSealMonthly} />}
      <Chibi src={chibis[type]} />
      <Paperclip src={selected ? pricePlanPaperClipHover : pricePlanPaperClipDefault} />
      <Blurb selected={selected}>{affiliate ? affiliateBlurbs[type] : (language === 'Japanese' ? blurbs : blurbsKr)[type]}</Blurb>
      <PlanTitle selected={selected}>{_.capitalize(type)}</PlanTitle>
      {affiliate && type !== 'free' && <CrossedPrice selected={selected} type={type}>{`${base}/${interval}`}</CrossedPrice>}
      {language === 'Korean' && (type === 'free' || type === 'monthly') && <CrossedPrice selected={selected} type={type}>{' '}</CrossedPrice>}
      {language === 'Korean' && type === 'yearly' && <CrossedPrice selected={selected} type={type}>{`${(parseFloat(monthlyBase.substring(1)) * 12).toFixed(2)}/${interval}`}</CrossedPrice>}
      <Price selected={selected} type={type}>{priceText}</Price>
      {affiliate && type === 'free' && <CrossedPrice selected={selected} type={type}>{' '}</CrossedPrice>}
      {paymentStage && <ChangePlanButton>Change plan</ChangePlanButton>}
      {!paymentStage && language !== 'Korean' && (
        <Features selected={selected} language={language}>
          {(language === 'Japanese' ? affiliateFeatures : affiliateFeaturesKr)[type].map((feature, i) => <li key={i}>{feature}</li>)}
        </Features>
      )}
      {type !== 'free' && affiliate && !paymentStage && (
        <AffiliateDisclaimerText selected={selected}>
          *{price} for {interval === 'mo' ? '3 months' : '1 year'}, then {base}/{interval}
        </AffiliateDisclaimerText>
      )}
      {type === 'free' && affiliate && !isMobile && <AffiliateDisclaimerEmpty selected={selected} />}
    </PlanWrapper>
  )
}

export default Plan
